/**
 * <CECPStatusChip> — Muestra chip de estado CECP de un proveedor.
 *
 * Estados:
 *   🟢 ok
 *   🟡 advertencia (con tooltip "se bloqueará en Nd")
 *   🟠 bloqueado_oc
 *   🔴 bloqueado_pago
 *   🔴🟠 bloqueado_ambos
 *
 * Props:
 *   rut  (string)   - RUT del proveedor (preferido)
 *   proveedorId (string) - id alternativo si hay
 *   compact (bool)  - si true, sólo ícono + tooltip
 *   onBlockedSubmit (fn) - callback si está bloqueado (para frenar submit)
 */
import { useEffect, useState } from 'react';
import { Badge } from './badge';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from './tooltip';
import { ShieldAlert, ShieldCheck, Clock } from 'lucide-react';
import api from '../../lib/api';

export function CECPStatusChip({ rut, proveedorId, compact = false }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!rut && !proveedorId) { setStatus(null); return; }
    let cancelled = false;
    setLoading(true);
    const params = {};
    if (proveedorId) params.proveedor_id = proveedorId;
    if (rut) params.rut = rut;
    api.get('/conciliacion/estado-proveedor', { params })
      .then((res) => { if (!cancelled) setStatus(res.data); })
      .catch(() => { if (!cancelled) setStatus({ estado: 'ok' }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [rut, proveedorId]);

  if (loading || !status) return null;
  if (status.estado === 'ok') {
    return compact ? null : (
      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]" data-testid="cecp-chip-ok">
        <ShieldCheck className="w-3 h-3 mr-1" /> CECP OK
      </Badge>
    );
  }

  const confs = {
    advertencia:     { label: `🟡 Bloqueo en ${status.dias_hasta_bloqueo ?? '?'}d`, cls: 'bg-yellow-50 text-yellow-800 border-yellow-300', icon: Clock },
    bloqueado_oc:    { label: '🟠 Bloqueado OC',     cls: 'bg-orange-50 text-orange-800 border-orange-300', icon: ShieldAlert },
    bloqueado_pago:  { label: '🔴 Bloqueado Pago',   cls: 'bg-red-50 text-red-800 border-red-300',           icon: ShieldAlert },
    bloqueado_ambos: { label: '🔴🟠 Bloqueado',       cls: 'bg-red-50 text-red-800 border-red-300',           icon: ShieldAlert },
  };
  const c = confs[status.estado] || confs.advertencia;
  const Icon = c.icon;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="outline"
            className={`${c.cls} text-[11px] cursor-help`}
            data-testid={`cecp-chip-${status.estado}`}
          >
            <Icon className="w-3 h-3 mr-1" />
            {compact ? '' : c.label}
          </Badge>
        </TooltipTrigger>
        <TooltipContent className="max-w-sm">
          <div className="text-xs">
            {status.mensaje || c.label}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default CECPStatusChip;
